import { Identifier, ReduxState, ResourceContextProvider, toggleListItemExpand, useGetMainList, useGetOne, useListContext, useListFilterContext } from "ra-core"
import { createContext, FC, MutableRefObject, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import { isEqual } from 'lodash'
import { DataCatalogResponseDto, EncryptionRuleDataTypeMappingResponseDto, EncryptMappingStatusStatisticsResponseDto, EnumOptionResponseDto, PrimaryClassificationStatisticsResponseDto } from "~/gen/api/governance"
import { RecPatch } from "~/dataProvider/tools"
import { pages, sorts } from "~/utils/paramsWithGetMainList"
import { Criteria, DataCatalogInternalMutableState, EncryptRuleMap } from "./types"
import { aggregateCriterias } from "./criteriaReducers"
import { BulkEditModifiers, useBulkEdit } from "./useBulkEdit"
import { useDispatch, useSelector } from "react-redux"
import DataClassifications from "~/pages/settings/governance/DataClassifications"
import { RetentionRules } from "~/pages/governance/RetentionRules"
import { ReasonCollect } from "~/pages/governance/ReasonCollect"


export interface DataCatalogControllerProps extends BulkEditModifiers {
  criterias: Criteria[],
  pushCriteria: (c: Criteria) => void,
  removeCriteria: (c: Criteria) => void,
  criteriaLockedDown: boolean,
  
  classificationStatistics?: PrimaryClassificationStatisticsResponseDto,
  encryptStatusStatistics?: EncryptMappingStatusStatisticsResponseDto,
  databaseNameData: Record<number, EnumOptionResponseDto>,
  
  goMaintenancePage: (x: 'classification'|'retentionRule'|'reason4collect') => void,
  closeMaintenancePage: () => void,
  maintenancePageComponent: ReactNode,
  
  getFieldEncryptRules: (x: DataCatalogResponseDto) => EncryptionRuleDataTypeMappingResponseDto[],
  bulkEditOpen: boolean,
  bulkEditCount: number,
  bulkEditFullChecked: boolean,
  
  isRowExpandable: (x:DataCatalogResponseDto&RecPatch) => boolean,
  cancelSingleEdit: (x: DataCatalogResponseDto&RecPatch) => void,

  tableRef: MutableRefObject<HTMLTableElement>,
}

export const DataCatalogContext = createContext<DataCatalogControllerProps>({
  criterias: [],
  pushCriteria: (c: Criteria) => {},
  removeCriteria: (c: Criteria) => {},
  criteriaLockedDown: false,
  classificationStatistics: undefined,
  encryptStatusStatistics: undefined,
  goMaintenancePage: () => {},
  databaseNameData: {},
  handleSelectAll: () => {},
  getFieldEncryptRules: () => [],
  bulkEditEncryptRules: [],
  bulkEditMaskingKind: {kind:'not_support'},
  onToggleItem: () => {},
  cancelBulkEdit: () => {},
  startBulkEdit: () => {},
  retrieveAllSelectedIds: () => [],
  removeManyFromDraft: () => {},
  bulkEditOpen: false,
  bulkEditCount: 0,
  bulkEditFullChecked: false,
  isRowExpandable: () => false,
  cancelSingleEdit: () => {},
  closeMaintenancePage: () => {},
  maintenancePageComponent: null,
  tableRef: {current:null} as any,
})


export const DataCatalogContextProvider: FC = ({ children }) => {
  const value = useDataCatalogController()
  return (
    <DataCatalogContext.Provider value={value}>
      {children}
    </DataCatalogContext.Provider>
  )
}

export const useDataCatalogController = (): DataCatalogControllerProps => {
  // search criterias
  // statistics data
  // filter options data
  // field edit data
  // bulk action data
  const innerStateRef = useRef<DataCatalogInternalMutableState>({
    filtersObj: {},
    criteriasQueue: [],
    setFilters: () => {},
    lockdown: false,
    bulkEdit: {
      open: false,
      count: 0,
      fullChecked: false,
      signature: '{}',
      draft: { encryptable:{}, notEncryptable:{} },
    },
    databaseMap: {},
    pageData: {},
    encryptRuleMap: {},
    expanded: [],
  })
  const innerState = innerStateRef.current // should be immutable and stable
  const [innerVer, setInnerVer] = useState(new Date())
  
  const { setFilters: unstableSetFilters } = useListFilterContext()
  innerState.setFilters = unstableSetFilters  
  const listCtrls = useListContext()
  const { onUnselectItems, refetch, ids } = listCtrls
  
  const { data: databaseNameData } = useGetMainList<EnumOptionResponseDto&RecPatch>('govrnc-databases', pages(), sorts(), {}, {action:'DATA_CATA_CTX'})
  innerState.databaseMap = databaseNameData || {}

  const { data: encryptRuleMapData } = useGetMainList<EncryptRuleMap>('govrnc-encrypt-rule-mapping', pages(), sorts(), {}, {action:'DATA_CATA_CTX'})
  innerState.encryptRuleMap = encryptRuleMapData ? (encryptRuleMapData['1']?.map || {}) : {};

  const pushCriteria = useCallback((c: Criteria) => {
    if (innerState.lockdown || innerState.criteriasQueue.some(cq => isEqual(cq, c))) { return }

    innerState.criteriasQueue.push(c)
    innerState.filtersObj = aggregateCriterias(innerState.criteriasQueue)
    innerState.bulkEdit.draft = { encryptable:{}, notEncryptable:{} }
    innerState.bulkEdit.signature = JSON.stringify(innerState.filtersObj)
    onUnselectItems()
    
    innerState.setFilters({ ...innerState.filtersObj }, [])
    setInnerVer(new Date())
  }, [innerState])
  
  const removeCriteria = useCallback((c: Criteria) => {
    if (innerState.lockdown) { return }

    const i = innerState.criteriasQueue.indexOf(c)
    if (i >= 0) {
      innerState.criteriasQueue.splice(i, 1)
      innerState.filtersObj = aggregateCriterias(innerState.criteriasQueue)
      innerState.bulkEdit.draft = { encryptable:{}, notEncryptable:{} }
      innerState.bulkEdit.signature = JSON.stringify(innerState.filtersObj)  
      onUnselectItems()

      innerState.setFilters({ ...innerState.filtersObj }, [])
      setInnerVer(new Date())
    }
  }, [innerState])

  const criteriasOut = useMemo(() => [...innerState.criteriasQueue], [innerVer, innerState])

  const { data: classificationStatistics } = useGetOne<PrimaryClassificationStatisticsResponseDto&RecPatch>('govrnc-catalog-classi-statics', '1')
  const { data: encryptStatusStatistics } = useGetOne<EncryptMappingStatusStatisticsResponseDto&RecPatch>('govrnc-catalog-encrypt-statics', '1')

  const [maintenancePageComponent, setMaintenancePageComponent]= useState<ReactNode>(null)
  const goMaintenancePage = useCallback((x: 'classification'|'retentionRule'|'reason4collect') => {
    if (x === 'classification') {
      setMaintenancePageComponent(
        <ResourceContextProvider value="settings/governance/data-classification">
          <DataClassifications resource="settings/governance/data-classification" />
        </ResourceContextProvider>
      )
    } else if (x === 'retentionRule') {
      setMaintenancePageComponent(
        <ResourceContextProvider value="govrnc-retention-rule">
          <RetentionRules resource="govrnc-retention-rule" />
        </ResourceContextProvider>
      )
    } else if (x === 'reason4collect') {
      setMaintenancePageComponent(
        <ResourceContextProvider value="govrnc-reason-collect">
          <ReasonCollect resource="govrnc-reason-collect" />
        </ResourceContextProvider>
      )
    }
  }, [])
  const closeMaintenancePage = useCallback(() => {
    setMaintenancePageComponent(null)
  }, [])


  const getFieldEncryptRules = useCallback((x: DataCatalogResponseDto) => {
    if (!x || Object.keys(innerState.databaseMap).length === 0) { return [] }
    const databaseType = getDatabaseType(x.databaseId || 0)
    const l1Key = `${databaseType}-${x.type}`
    return innerState.encryptRuleMap[l1Key] ? [...(innerState.encryptRuleMap[l1Key])] : []
  }, [])
  

  const getDatabaseType = (databaseId: number) => {
    const database = innerState.databaseMap[databaseId]
    return database.databaseType
  }
  
  const bulkEditModifiers = useBulkEdit(innerState, listCtrls)

  const dispatch = useDispatch()

  const tableRef = useRef<HTMLTableElement>(null as any)
  const idsRef = useRef<Identifier[]>([])
  idsRef.current = ids
  const prevExpanded = useRef<Identifier[]>([])

  innerState.expanded = useSelector<ReduxState, Identifier[]|undefined>(store => store.admin.resources['governance/data-catalog']?.list.expanded) || []
  useEffect(() => {
    if (innerState.expanded.length > 0 && !innerState.lockdown) {
      innerState.lockdown = true
      setInnerVer(new Date())
    }
    if (innerState.expanded.length === 0) {
      if (innerState.lockdown) {
        innerState.lockdown = false
        setInnerVer(new Date())
      }
      if (prevExpanded.current.length > 0 && tableRef.current) {
        const id = prevExpanded.current[0]
        const i = idsRef.current.indexOf(id)
        const tr = tableRef.current.querySelector(`tbody tr.RaDatagrid-row:nth-of-type(${i + 1})`)
        if (tr) {
          tr.classList.add('OdHighlighted')
          setTimeout(() => tr.classList.add('OdFading'), 50)
          setTimeout(() => tr.classList.remove('OdHighlighted'), 2000)
          setTimeout(() => tr.classList.remove('OdFading'), 4000)
        }
      }
    } 
    prevExpanded.current = [...innerState.expanded]
  }, [innerState.expanded.length])
  
  useEffect(() => {
    // when page tear down, clear expanded state
    return () => {
      for (let id of innerState.expanded) {
        dispatch(toggleListItemExpand('governance/data-catalog', id))
      }
    }
  }, [])

  const cancelSingleEdit = useCallback((x: DataCatalogResponseDto&RecPatch) => {
    if (innerState.expanded.indexOf(x.id) > -1) {
      innerState.lockdown = false
      dispatch(toggleListItemExpand('governance/data-catalog', x.id))
      setInnerVer(new Date())
      refetch()
    }
  }, [])

  const isRowExpandable = useCallback((x:DataCatalogResponseDto&RecPatch) => {
    return !innerState.lockdown || innerState.expanded.indexOf(x.id) > -1
  }, [])

  return {
    criterias: criteriasOut,
    pushCriteria,
    removeCriteria,
    criteriaLockedDown: innerState.lockdown,
    classificationStatistics,
    encryptStatusStatistics,
    databaseNameData: databaseNameData || {},
    getFieldEncryptRules,
    bulkEditOpen: innerState.bulkEdit.open,
    bulkEditCount: innerState.bulkEdit.count,
    bulkEditFullChecked: innerState.bulkEdit.fullChecked,
    isRowExpandable,
    cancelSingleEdit,
    maintenancePageComponent,
    goMaintenancePage,
    closeMaintenancePage,
    tableRef,

    ...bulkEditModifiers,
  }
}

export const useDataCatalogContext = () => useContext(DataCatalogContext);

